/**
 * ExtractedFieldsList — vue des champs extraits + override humain, parent-agnostique.
 *
 * 9.T.5.v3.3 (audit PO Q3 + Q5 hard-break + Q7 relocation) :
 *   - Props `submissionId` REMPLACÉ par `{ parentKind, parentId }` — la
 *     source de vérité est l'extraction elle-même (`extractionId`), le
 *     parent sert uniquement au dispatch d'invalidation post-override.
 *   - i18n namespace neutre `extracted_fields.*` (Q4 migration complète).
 *
 * Doctrine §2.5 focus-visible + §2.7 i18n active voice + WIG aria-live.
 */

import type { ExtractedFieldRow, ExtractionParentKind } from "@kollectivia/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { useExtractionFields } from "../../lib/queries/extractions";

import { ExtractedFieldEditor } from "./ExtractedFieldEditor";

export type ExtractedFieldsListProps = {
  extractionId: string;
  parentKind: ExtractionParentKind;
  parentId: string;
};

export function ExtractedFieldsList({
  extractionId,
  parentKind,
  parentId,
}: ExtractedFieldsListProps) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const q = useExtractionFields(extractionId);

  const [onlyNeedsReview, setOnlyNeedsReview] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  if (q.isLoading) {
    return (
      <p className="text-sm text-slate-400" aria-live="polite">
        {t("app.loading")}
      </p>
    );
  }

  if (q.error) {
    return (
      <p className="text-sm text-red-700" role="alert">
        {q.error instanceof ApiException
          ? getApiErrorMessage(q.error, lang) || t("errors.generic")
          : t("errors.generic")}
      </p>
    );
  }

  const all = q.data?.items ?? [];
  const items = onlyNeedsReview ? all.filter((f) => f.needs_review) : all;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <label className="inline-flex items-center gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={onlyNeedsReview}
            onChange={(e) => setOnlyNeedsReview(e.target.checked)}
            className="focus-visible:ring-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
          />
          {t("extracted_fields.filter_needs_review")}
        </label>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-slate-500">{t("extracted_fields.empty")}</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((f) => (
            <FieldLine
              key={f.id}
              field={f}
              extractionId={extractionId}
              parentKind={parentKind}
              parentId={parentId}
              isEditing={editingId === f.id}
              onEdit={() => setEditingId(f.id)}
              onCloseEdit={() => setEditingId(null)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function FieldLine({
  field,
  extractionId,
  parentKind,
  parentId,
  isEditing,
  onEdit,
  onCloseEdit,
}: {
  field: ExtractedFieldRow;
  extractionId: string;
  parentKind: ExtractionParentKind;
  parentId: string;
  isEditing: boolean;
  onEdit: () => void;
  onCloseEdit: () => void;
}) {
  const { t } = useTranslation();
  return (
    <li className="min-w-0 py-2.5">
      <div className="flex min-w-0 items-baseline justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-baseline gap-2">
          <span className="break-all font-mono text-xs text-slate-500" translate="no">
            {field.field_key}
          </span>
          {field.needs_review && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800">
              {t("extracted_fields.needs_review")}
            </span>
          )}
          {field.override_by && (
            <span className="bg-brand-100 text-brand-800 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide">
              {t("extracted_fields.overridden")}
            </span>
          )}
        </div>
        {!isEditing && (
          <button
            type="button"
            className="btn-ghost focus-visible:ring-brand-500 text-[11px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            onClick={onEdit}
          >
            {t("extracted_fields.edit.open")}
          </button>
        )}
      </div>
      <div className="mt-0.5 flex min-w-0 flex-wrap items-baseline gap-x-3">
        <span
          className="text-sm font-medium text-slate-800"
          style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
        >
          {formatValue(field.value)}
          {field.unit ? (
            <span className="ml-1 text-xs font-normal text-slate-500">{field.unit}</span>
          ) : null}
        </span>
        {typeof field.confidence === "number" && (
          <span className="text-xs text-slate-500" style={{ fontVariantNumeric: "tabular-nums" }}>
            {t("extracted_fields.confidence")}: {(field.confidence * 100).toFixed(0)}%
          </span>
        )}
        {typeof field.agreement === "number" && (
          <span className="text-xs text-slate-500" style={{ fontVariantNumeric: "tabular-nums" }}>
            {t("extracted_fields.agreement")}: {(field.agreement * 100).toFixed(0)}%
          </span>
        )}
      </div>
      {(field.citation_quote || field.citation_page) && (
        <p className="mt-1 max-w-2xl border-l-2 border-slate-200 pl-2 text-xs italic text-slate-500">
          {field.citation_page ? `p. ${field.citation_page} — ` : null}
          {field.citation_quote ?? ""}
        </p>
      )}
      {isEditing && (
        <ExtractedFieldEditor
          field={field}
          extractionId={extractionId}
          parentKind={parentKind}
          parentId={parentId}
          onClose={onCloseEdit}
        />
      )}
    </li>
  );
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
    return String(v);
  }
  // 9.T.5.v3.6 QA polish round 4 — array de strings courtes : joindre avec `,·`
  // pour permettre le wrap au niveau des séparateurs (au lieu d'un JSON stringify
  // en une seule ligne qui casse le layout du modal).
  if (Array.isArray(v) && v.every((x) => typeof x === "string" || typeof x === "number")) {
    if (v.length === 0) return "[]";
    return v.join(", ");
  }
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}
